/**
 * Error Display Component
 *
 * Comprehensive error display with different variants, retry actions, and detailed error info.
 * Used throughout the application for error states.
 */

import { useState } from "react";
import {
  ExclamationTriangleIcon,
  XCircleIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import Button from "./Button";

// Error types and their styling
const ERROR_TYPES = {
  error: {
    icon: XCircleIcon,
    bgColor: "bg-red-50",
    borderColor: "border-red-200",
    iconColor: "text-red-500",
    titleColor: "text-red-800",
    textColor: "text-red-700",
  },
  warning: {
    icon: ExclamationTriangleIcon,
    bgColor: "bg-yellow-50",
    borderColor: "border-yellow-200",
    iconColor: "text-yellow-500",
    titleColor: "text-yellow-800",
    textColor: "text-yellow-700",
  },
  info: {
    icon: InformationCircleIcon,
    bgColor: "bg-blue-50",
    borderColor: "border-blue-200",
    iconColor: "text-blue-500",
    titleColor: "text-blue-800",
    textColor: "text-blue-700",
  },
};

// Size variants
const SIZES = {
  sm: {
    container: "p-3",
    icon: "h-4 w-4",
    title: "text-sm font-medium",
    text: "text-xs",
    button: "text-xs px-2 py-1",
  },
  md: {
    container: "p-4",
    icon: "h-5 w-5",
    title: "text-sm font-medium",
    text: "text-sm",
    button: "text-sm px-3 py-2",
  },
  lg: {
    container: "p-6",
    icon: "h-6 w-6",
    title: "text-base font-medium",
    text: "text-sm",
    button: "text-sm px-4 py-2",
  },
};

/**
 * Main Error Display Component
 */
export default function ErrorDisplay({
  error,
  type = "error",
  size = "md",
  title,
  message,
  details,
  showDetails = false,
  onRetry,
  onDismiss,
  retryText = "Try Again",
  dismissText = "Dismiss",
  className = "",
  children,
  ...props
}) {
  const [isDetailsOpen, setIsDetailsOpen] = useState(showDetails);

  // Determine error info
  const errorInfo = ERROR_TYPES[type] || ERROR_TYPES.error;
  const sizeInfo = SIZES[size] || SIZES.md;
  const IconComponent = errorInfo.icon;

  // Extract error information
  const errorTitle = title || getErrorTitle(error, type);
  const errorMessage = message || getErrorMessage(error);
  const errorDetails = details || getErrorDetails(error);

  return (
    <div
      className={`rounded-lg border ${errorInfo.bgColor} ${errorInfo.borderColor} ${sizeInfo.container} ${className}`}
      {...props}
    >
      <div className="flex items-start">
        {/* Icon */}
        <div className="flex-shrink-0">
          <IconComponent
            className={`${sizeInfo.icon} ${errorInfo.iconColor}`}
          />
        </div>

        {/* Content */}
        <div className="ml-3 flex-1">
          {/* Title */}
          {errorTitle && (
            <h3 className={`${sizeInfo.title} ${errorInfo.titleColor}`}>
              {errorTitle}
            </h3>
          )}

          {/* Message */}
          {errorMessage && (
            <div
              className={`${errorTitle ? "mt-1" : ""} ${sizeInfo.text} ${errorInfo.textColor}`}
            >
              {errorMessage}
            </div>
          )}

          {/* Custom children */}
          {children && (
            <div className={`${errorTitle || errorMessage ? "mt-2" : ""}`}>
              {children}
            </div>
          )}

          {/* Actions */}
          {(onRetry || onDismiss || errorDetails) && (
            <div
              className={`${errorTitle || errorMessage || children ? "mt-3" : ""} flex flex-wrap gap-2`}
            >
              {onRetry && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onRetry}
                  className={`${sizeInfo.button} ${errorInfo.titleColor} border-current hover:bg-current hover:bg-opacity-10`}
                >
                  {retryText}
                </Button>
              )}

              {errorDetails && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsDetailsOpen(!isDetailsOpen)}
                  className={`${sizeInfo.button} ${errorInfo.titleColor}`}
                >
                  {isDetailsOpen ? "Hide Details" : "Show Details"}
                </Button>
              )}

              {onDismiss && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onDismiss}
                  className={`${sizeInfo.button} ${errorInfo.textColor}`}
                >
                  {dismissText}
                </Button>
              )}
            </div>
          )}

          {/* Error details */}
          {errorDetails && isDetailsOpen && (
            <div
              className={`mt-3 p-3 bg-white bg-opacity-50 rounded border ${errorInfo.borderColor}`}
            >
              <details className="cursor-pointer">
                <summary
                  className={`${sizeInfo.text} font-medium ${errorInfo.titleColor} select-none`}
                >
                  Technical Details
                </summary>
                <div
                  className={`mt-2 ${sizeInfo.text} ${errorInfo.textColor} font-mono whitespace-pre-wrap break-all`}
                >
                  {errorDetails}
                </div>
              </details>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Helper functions to extract error information
 */
function getErrorTitle(error, type) {
  if (!error) return null;

  // Custom titles based on error type
  if (error.code) {
    switch (error.code) {
      case "NETWORK_ERROR":
        return "Connection Error";
      case "VALIDATION_ERROR":
        return "Validation Error";
      case "NOT_FOUND":
        return "Not Found";
      case "UNAUTHORIZED":
        return "Access Denied";
      case "TIMEOUT":
        return "Request Timeout";
      default:
        break;
    }
  }

  // Default titles by type
  switch (type) {
    case "warning":
      return "Warning";
    case "info":
      return "Information";
    default:
      return "Error";
  }
}

function getErrorMessage(error) {
  if (!error) return null;

  if (typeof error === "string") {
    return error;
  }

  if (error.message) {
    return error.message;
  }

  if (error.errors && Array.isArray(error.errors)) {
    return error.errors.join(", ");
  }

  return "An unexpected error occurred";
}

function getErrorDetails(error) {
  if (!error) return null;

  if (typeof error === "string") {
    return null; // No additional details for string errors
  }

  const details = [];

  if (error.stack) {
    details.push(`Stack Trace:\n${error.stack}`);
  }

  if (error.code) {
    details.push(`Error Code: ${error.code}`);
  }

  if (error.path) {
    details.push(`Path: ${error.path}`);
  }

  if (error.field) {
    details.push(`Field: ${error.field}`);
  }

  if (error.timestamp) {
    details.push(`Timestamp: ${error.timestamp}`);
  }

  // Include any additional error properties
  const additionalProps = Object.keys(error).filter(
    (key) =>
      ![
        "message",
        "stack",
        "code",
        "path",
        "field",
        "timestamp",
        "name",
      ].includes(key)
  );

  additionalProps.forEach((prop) => {
    const value = error[prop];
    if (value !== undefined && value !== null) {
      details.push(`${prop}: ${JSON.stringify(value, null, 2)}`);
    }
  });

  return details.length > 0 ? details.join("\n\n") : null;
}

/**
 * Specialized Error Components
 */

/**
 * Network Error Component
 */
export function NetworkError({ onRetry, className = "", ...props }) {
  return (
    <ErrorDisplay
      type="error"
      title="Connection Error"
      message="Unable to connect to the server. Please check your internet connection and try again."
      onRetry={onRetry}
      retryText="Retry Connection"
      className={className}
      {...props}
    />
  );
}

/**
 * Validation Error Component
 */
export function ValidationError({
  errors = [],
  onRetry,
  className = "",
  ...props
}) {
  const errorList = Array.isArray(errors) ? errors : [errors];

  return (
    <ErrorDisplay
      type="warning"
      title="Validation Error"
      message={errorList.length === 1 ? errorList[0] : undefined}
      onRetry={onRetry}
      retryText="Fix Errors"
      className={className}
      {...props}
    >
      {errorList.length > 1 && (
        <ul className="mt-2 text-sm text-yellow-700 list-disc pl-5 space-y-1">
          {errorList.map((error, index) => (
            <li key={index}>{error}</li>
          ))}
        </ul>
      )}
    </ErrorDisplay>
  );
}

/**
 * Not Found Error Component
 */
export function NotFoundError({
  resource = "resource",
  onRetry,
  className = "",
  ...props
}) {
  return (
    <ErrorDisplay
      type="warning"
      title="Not Found"
      message={`The requested ${resource} could not be found.`}
      onRetry={onRetry}
      retryText="Try Again"
      className={className}
      {...props}
    />
  );
}

/**
 * Permission Error Component
 */
export function PermissionError({ onRetry, className = "", ...props }) {
  return (
    <ErrorDisplay
      type="warning"
      title="Access Denied"
      message="You don't have permission to access this resource."
      onRetry={onRetry}
      retryText="Retry"
      className={className}
      {...props}
    />
  );
}

/**
 * Generic API Error Component
 */
export function ApiError({ error, onRetry, className = "", ...props }) {
  return (
    <ErrorDisplay
      error={error}
      type="error"
      onRetry={onRetry}
      className={className}
      {...props}
    />
  );
}

/**
 * Inline Error Component (for forms)
 */
export function InlineError({ message, className = "", ...props }) {
  return (
    <div
      className={`flex items-center space-x-1 text-red-600 text-sm ${className}`}
      {...props}
    >
      <XCircleIcon className="h-4 w-4 flex-shrink-0" />
      <span>{message}</span>
    </div>
  );
}

// Export convenience components
ErrorDisplay.Network = NetworkError;
ErrorDisplay.Validation = ValidationError;
ErrorDisplay.NotFound = NotFoundError;
ErrorDisplay.Permission = PermissionError;
ErrorDisplay.Api = ApiError;
ErrorDisplay.Inline = InlineError;
